import React, { useState, useEffect } from "react";
import {
  Input,
  Select,
  Button,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Switch,
  Empty,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const { Option } = Select;
const { Text } = Typography;

const DynamicForm = ({ value, onChange }) => {
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [options, setOptions] = useState("");
  const [required, setRequired] = useState(false);

  useEffect(() => {
    if (value) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, "text/html");

        const parsedFields = [];

        const formGroups = doc.querySelectorAll("form > div");

        formGroups.forEach((group, index) => {
          const labelEl = group.querySelector("label");
          const inputEl = group.querySelector("input, select");

          if (!labelEl || !inputEl) return;

          const labelText = labelEl.textContent.replace("*", "").trim();
          const required = labelEl.innerHTML.includes("*");

          let type = inputEl.tagName.toLowerCase();

          if (type === "input") {
            type = inputEl.getAttribute("type") || "text";
          } else if (type === "select") {
            type = "select";
          }

          let options = [];

          if (type === "radio") {
            const radioInputs = group.querySelectorAll(`input[type="radio"]`);
            options = Array.from(radioInputs).map((r) => r.value);
          }

          if (type === "select") {
            const optionEls = group.querySelectorAll("option");
            options = Array.from(optionEls).map((o) => o.value);
          }

          const defaultFields = ["full name", "email", "contact number"];

          parsedFields.push({
            id: Date.now() + index,
            label: labelText,
            type,
            required,
            options,
            isDefault: defaultFields.includes(labelText.toLowerCase()),
          });
        });

        if (parsedFields.length) {
          setFields(parsedFields);
        }
      } catch (err) {
        console.error("Error parsing form template:", err);
      }
    }
  }, [value]);

  const [fields, setFields] = useState([
    {
      id: 1,
      label: "Full Name",
      type: "text",
      required: true,
      options: [],
      isDefault: true,
    },
    {
      id: 2,
      label: "Email",
      type: "email",
      required: true,
      options: [],
      isDefault: true,
    },
    {
      id: 3,
      label: "Contact Number",
      type: "text",
      required: true,
      options: [],
      isDefault: true,
    },
  ]);

  const generateHTML = (fieldsData) => {
    let html = `
  <div style="font-family: Arial, sans-serif; background:white; padding:40px; display:flex; justify-content:center;">
    <form style="background:#fff; padding:30px; border-radius:10px; width:100%; max-width:500px; box-shadow:0 8px 20px rgba(0,0,0,0.1);">
  `;

    fieldsData.forEach((field) => {
      const label = field.label?.toLowerCase();
      const nameAttr = field.label?.toLowerCase().replace(/\s+/g, "_");

      html += `
      <div style="margin-bottom:18px; display:flex; flex-direction:column;">
        <label style="font-weight:600; margin-bottom:6px; color:#444; text-transform:capitalize;">
          ${label}${field.required ? " <span style='color:red'>*</span>" : ""}
        </label>
    `;

      if (field.type === "radio") {
        field.options?.forEach((opt) => {
          html += `
          <div style="display:flex; align-items:center; margin-bottom:5px;">
            <input 
              type="radio" 
              name="${nameAttr}" 
              value="${opt.toLowerCase()}" 
              ${field.required ? "required" : ""} 
              style="margin-right:6px;"
            />
            <span>${opt.toLowerCase()}</span>
          </div>
        `;
        });
      } else if (field.type === "select") {
        html += `
        <select 
          name="${nameAttr}" 
          ${field.required ? "required" : ""} 
          style="padding:10px; border-radius:6px; border:1px solid #ccc; font-size:14px;"
        >
      `;

        field.options?.forEach((opt) => {
          html += `<option value="${opt.toLowerCase()}">${opt.toLowerCase()}</option>`;
        });

        html += `</select>`;
      } else if (field.type === "checkbox") {
        html += `
        <div style="display:flex; align-items:center;">
          <input 
            type="checkbox" 
            name="${nameAttr}" 
            ${field.required ? "required" : ""} 
            style="margin-right:6px;"
          />
          <span>${label}</span>
        </div>
      `;
      } else {
        html += `
        <input 
          type="${field.type.toLowerCase()}" 
          name="${nameAttr}" 
          ${field.required ? "required" : ""} 
          style="padding:10px; border-radius:6px; border:1px solid #ccc; font-size:14px;"
        />
      `;
      }

      html += `</div>`;
    });

    html += `
      <button 
        type="submit" 
       style="width:100%; padding:6px; background:white; color:black; border:1px solid black; border-radius:6px; font-size:15px; cursor:pointer; margin-top:10px;"
      >
        Submit
      </button>
    </form>
  </div>
  `;

    return html;
  };

  useEffect(() => {
    if (onChange) {
      const html = generateHTML(fields);
      onChange(html);
    }
  }, [fields]);

  const addField = () => {
    if (!label.trim()) return;

    const newField = {
      id: Date.now(),
      label,
      type,
      required,
      options:
        type === "radio" || type === "select"
          ? options.split(",").map((opt) => opt.trim())
          : [],
    };

    setFields([...fields, newField]);
    setLabel("");
    setOptions("");
    setType("text");
    setRequired(false);
  };

  const deleteField = (id) => {
    setFields((prev) =>
      prev.filter((field) => {
        if (field.id === id && field.isDefault) return true;
        return field.id !== id;
      }),
    );
  };

  return (
    <Row gutter={24}>
      <Col span={10}>
        <Card title="Form Builder" bordered style={{ borderRadius: 12 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Input
              placeholder="Field Label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              style={{ border: "1px solid #d9d9d9", borderRadius: 6 }}
            />

            <Select value={type} onChange={setType} style={{ borderRadius: 6 }}>
              <Option value="text">Text</Option>
              <Option value="number">Number</Option>
              <Option value="email">Email</Option>
              <Option value="checkbox">Checkbox</Option>
              <Option value="radio">Radio</Option>
              <Option value="range">Range</Option>
              <Option value="date">Date</Option>
              <Option value="select">Select</Option>
              <Option value="file">File</Option>
            </Select>

            {(type === "radio" || type === "select") && (
              <Input
                placeholder="Options (comma separated)"
                value={options}
                onChange={(e) => setOptions(e.target.value)}
                style={{ border: "1px solid #d9d9d9", borderRadius: 6 }}
              />
            )}

            <Space>
              <Text>Required</Text>
              <Switch checked={required} onChange={(val) => setRequired(val)} />
            </Space>

            <Button
              className="btn-gradient"
              block
              icon={<PlusOutlined />}
              onClick={addField}
            >
              Add Field
            </Button>
          </Space>
        </Card>
      </Col>

      <Col span={14}>
        <Card title="Preview" style={{ minHeight: 400, borderRadius: 12 }}>
          {fields.length === 0 ? (
            <Empty description="No fields added yet" />
          ) : (
            fields.map((field) => (
              <div
                key={field.id}
                style={{
                  marginBottom: 20,
                  padding: 12,
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                }}
              >
                {!field.isDefault && (
                  <DeleteOutlined
                    onClick={() => deleteField(field.id)}
                    style={{ float: "right", color: "red", cursor: "pointer" }}
                  />
                )}
                <label>
                  <strong className="capitalize">{field.label}</strong>
                  {field.required && <span style={{ color: "red" }}> *</span>}
                </label>

                <div style={{ marginTop: 8 }}>
                  {field.type === "radio" &&
                    field.options.map((opt, i) => (
                      <label key={i} style={{ marginRight: 10 }}>
                        <input type="radio" /> {opt}
                      </label>
                    ))}

                  {field.type === "select" && (
                    <select
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                      }}
                    >
                      {field.options.map((opt, i) => (
                        <option key={i}>{opt}</option>
                      ))}
                    </select>
                  )}

                  {field.type === "checkbox" && <input type="checkbox" />}

                  {!["radio", "select", "checkbox"].includes(field.type) && (
                    <input
                      type={field.type}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                      }}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default DynamicForm;
